/**
 * Trust Pilot Section - Drag to Scroll
 * Enables horizontal scrolling via mouse drag and touch gestures
 */
(function() {
  'use strict';

  class TrustPilotDragScroll {
    constructor(container) {
      this.container = container;
      this.isDragging = false;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.hasMoved = false;
      this.autoScrollInterval = null;
      this.autoScrollDelay = 5000; // 3 seconds between scrolls
      this.isPaused = false;

      // Bind methods
      this.handleDragStart = this.handleDragStart.bind(this);
      this.handleDragMove = this.handleDragMove.bind(this);
      this.handleDragEnd = this.handleDragEnd.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.startAutoScroll = this.startAutoScroll.bind(this);
      this.stopAutoScroll = this.stopAutoScroll.bind(this);
      this.scrollToNextItem = this.scrollToNextItem.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.snapToNearestItem = this.snapToNearestItem.bind(this);

      this.init();
    }

    init() {
      // Prevent image dragging
      const images = this.container.querySelectorAll('img');
      images.forEach(img => img.setAttribute('draggable', 'false'));

      // Mouse events
      this.container.addEventListener('mousedown', this.handleDragStart);
      document.addEventListener('mousemove', this.handleDragMove);
      document.addEventListener('mouseup', this.handleDragEnd);
      document.addEventListener('mouseleave', this.handleDragEnd);

      // Touch events
      this.container.addEventListener('touchstart', this.handleDragStart, { passive: false });
      this.container.addEventListener('touchmove', this.handleDragMove, { passive: false });
      this.container.addEventListener('touchend', this.handleDragEnd);
      this.container.addEventListener('touchcancel', this.handleDragEnd);

      // Prevent clicks during drag
      this.container.addEventListener('click', this.handleClick, true);

      // Pause auto-scroll on hover
      this.container.addEventListener('mouseenter', this.handleMouseEnter);
      this.container.addEventListener('mouseleave', this.handleMouseLeave);

      // Start auto-scroll
      this.startAutoScroll();
    }

    handleDragStart(e) {
      // Get the appropriate X coordinate (mouse or touch)
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      
      this.isDragging = true;
      this.hasMoved = false;
      this.startX = clientX;
      this.startScrollLeft = this.container.scrollLeft;

      this.container.style.cursor = 'grabbing';
      this.container.classList.add('dragging');
      this.container.style.scrollBehavior = 'auto';

      // Pause auto-scroll while dragging
      this.stopAutoScroll();

      e.preventDefault();
    }

    handleDragMove(e) {
      if (!this.isDragging) return;

      // Get the appropriate X coordinate (mouse or touch)
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const walk = this.startX - clientX;

      if (Math.abs(walk) > 5) {
        this.hasMoved = true;
      }

      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      let newScrollPosition = this.startScrollLeft + walk;

      // Apply boundaries
      newScrollPosition = Math.max(0, Math.min(newScrollPosition, maxScroll));

      this.container.scrollLeft = newScrollPosition;

      e.preventDefault();
    }

    handleDragEnd(e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.container.style.cursor = 'grab';
      this.container.classList.remove('dragging');
      this.container.style.scrollBehavior = 'smooth';

      // Reset hasMoved after a short delay to allow click prevention
      if (this.hasMoved) {
        setTimeout(() => {
          this.hasMoved = false;
        }, 100);
      }

      // Snap to nearest item if user dragged
      if (this.hasMoved) {
        this.snapToNearestItem(() => {
          // Resume auto-scroll after snapping is complete
          if (!this.isPaused) {
            this.startAutoScroll();
          }
        });
      } else {
        // Resume auto-scroll if not paused
        if (!this.isPaused) {
          this.startAutoScroll();
        }
      }
    }

    handleClick(e) {
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    scrollToNextItem() {
      const items = this.container.querySelectorAll('.trust-banner__item');
      if (items.length === 0) return;

      const containerRect = this.container.getBoundingClientRect();
      const containerRight = containerRect.right;
      
      // Check if the last element is already visible
      const lastItem = items[items.length - 1];
      const lastItemRect = lastItem.getBoundingClientRect();
      
      // If the last item's right edge is visible (within or at container's right edge)
      if (lastItemRect.right <= containerRight + 1) { // +1 for rounding tolerance
        // Scroll back to the first slide
        this.container.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
        return;
      }

      // Get the first visible item to calculate width
      let firstVisibleItem = null;
      
      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        // Check if item is at least partially visible
        if (itemRect.right > containerRect.left && itemRect.left < containerRect.right) {
          firstVisibleItem = items[i];
          break;
        }
      }

      // Fallback to first item if none found
      if (!firstVisibleItem) {
        firstVisibleItem = items[0];
      }

      // Calculate item width including gap
      const itemRect = firstVisibleItem.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(this.container);
      const gap = parseFloat(computedStyle.gap) || 8;
      const itemWidth = itemRect.width + gap;

      // Calculate next scroll position
      const currentScroll = this.container.scrollLeft;
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      let nextScroll = currentScroll + itemWidth;

      // Ensure we don't scroll past the end
      nextScroll = Math.min(nextScroll, maxScroll);

      // Smooth scroll to next position
      this.container.scrollTo({
        left: nextScroll,
        behavior: 'smooth'
      });
    }

    startAutoScroll() {
      // Clear any existing interval
      this.stopAutoScroll();
      
      // Only start if not dragging and not paused
      if (!this.isDragging && !this.isPaused) {
        this.autoScrollInterval = setInterval(() => {
          // Double-check we're still not dragging or paused
          if (!this.isDragging && !this.isPaused) {
            this.scrollToNextItem();
          }
        }, this.autoScrollDelay);
      }
    }

    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    }

    handleMouseEnter() {
      this.isPaused = true;
      this.stopAutoScroll();
    }

    handleMouseLeave() {
      this.isPaused = false;
      if (!this.isDragging) {
        this.startAutoScroll();
      }
    }

    snapToNearestItem(callback) {
      const items = this.container.querySelectorAll('.trust-banner__item');
      if (items.length === 0) {
        if (callback) callback();
        return;
      }

      const containerRect = this.container.getBoundingClientRect();
      const containerLeft = containerRect.left;
      
      // Find the item that's closest to the left edge of the container
      let nearestItem = null;
      let minDistance = Infinity;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemRect = item.getBoundingClientRect();
        const itemLeft = itemRect.left;
        
        // Calculate distance from container's left edge to item's left edge
        const distance = Math.abs(itemLeft - containerLeft);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestItem = item;
        }
      }

      if (!nearestItem) {
        if (callback) callback();
        return;
      }

      // Calculate the scroll position needed to align the nearest item's left edge 
      // with the container's left edge
      // We calculate based on viewport positions to account for all padding/gap correctly
      const nearestItemRect = nearestItem.getBoundingClientRect();
      const currentScroll = this.container.scrollLeft;
      
      // Calculate how much we need to scroll to align item's left edge with container's left edge
      // The difference in viewport positions tells us the scroll offset needed
      // containerRect was already declared above, reuse containerLeft
      const scrollOffset = nearestItemRect.left - containerLeft;
      let targetScroll = currentScroll + scrollOffset;

      // Ensure we don't scroll past the end
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      // Smooth scroll to the nearest item
      this.container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });

      // Wait for scroll animation to complete before calling callback
      if (callback) {
        // Estimate scroll duration (typically 300-500ms for smooth scroll)
        setTimeout(callback, 500);
      }
    }

    destroy() {
      // Stop auto-scroll
      this.stopAutoScroll();

      // Remove event listeners
      this.container.removeEventListener('mousedown', this.handleDragStart);
      document.removeEventListener('mousemove', this.handleDragMove);
      document.removeEventListener('mouseup', this.handleDragEnd);
      document.removeEventListener('mouseleave', this.handleDragEnd);
      this.container.removeEventListener('touchstart', this.handleDragStart);
      this.container.removeEventListener('touchmove', this.handleDragMove);
      this.container.removeEventListener('touchend', this.handleDragEnd);
      this.container.removeEventListener('touchcancel', this.handleDragEnd);
      this.container.removeEventListener('click', this.handleClick, true);
      this.container.removeEventListener('mouseenter', this.handleMouseEnter);
      this.container.removeEventListener('mouseleave', this.handleMouseLeave);
    }
  }

  // Initialize on DOM ready
  function initTrustPilotDragScroll() {
    const containers = document.querySelectorAll('.trust-banner__content');
    containers.forEach(container => {
      // Check if already initialized
      if (!container.dataset.dragScrollInitialized) {
        container.dataset.dragScrollInitialized = 'true';
        new TrustPilotDragScroll(container);
      }
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTrustPilotDragScroll);
  } else {
    initTrustPilotDragScroll();
  }

  // Re-initialize for Shopify section reloading
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId && document.querySelector(`#TrustPilot-${event.detail.sectionId} .trust-banner__content`)) {
        initTrustPilotDragScroll();
      }
    });
  }

  // Also listen for general section loads
  document.addEventListener('shopify:section:load', initTrustPilotDragScroll);
})();


